import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged, auth } from '../firebase';
import { Check, X, Search, Loader2, Users, Clock, AlertTriangle, Bookmark, BookmarkPlus, ChevronDown } from 'lucide-react';
import { getAssignmentHistory, getDriverSelectionTemplate, saveDriverSelectionTemplate, buildDriverSelectionTemplateDocId } from '../services/firebaseService';
import { schedulerApiFetch } from '../api/schedulerClient';
import { formatInTimeZone } from 'date-fns-tz';

const FUNDING_EXPIRED_OPTION = 'Expired';

function normalizeId(v) {
  if (!v) return '';
  if (typeof v === 'object') return normalizeId(v._id || v.id || v.$oid);
  return String(v).trim();
}

function getServiceAreaName(serviceAreaMap, serviceAreaId) {
  const id = normalizeId(serviceAreaId);
  if (!id) return '';
  return String(serviceAreaMap?.[id.toLowerCase()] || serviceAreaMap?.[id] || '').trim();
}

function getFundingSourceName(cooperateNames, cooperateId) {
  const id = normalizeId(cooperateId);
  if (!id) return '';
  return String(cooperateNames?.[id] || cooperateNames?.[id.toLowerCase()] || '').trim();
}

function looksLikeObjectId(raw) {
  const s = String(raw || '').trim();
  return /^[a-fA-F0-9]{24}$/.test(s);
}

function parseUtcDate(raw) {
  if (!raw) return null;
  let s = String(raw).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) s = `${s}T00:00:00Z`;
  else if (!/Z$|[+-]\d{2}:?\d{2}$/.test(s)) s = `${s}Z`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function zonedStamp(date, timeZone) {
  try {
    return formatInTimeZone(date, timeZone, "yyyy-MM-dd'T'HH:mm:ss");
  } catch {
    return '';
  }
}

function isFundingExpired(fs, timeZone) {
  if (!fs) return false;
  const tz = timeZone || 'America/Los_Angeles';
  const nowStamp = zonedStamp(new Date(), tz);
  if (!nowStamp) return false;
  const start = parseUtcDate(fs.validity_start);
  const end = parseUtcDate(fs.validity_end);
  if (start) {
    const startStamp = zonedStamp(start, tz);
    if (startStamp && nowStamp < startStamp) return true;
  }
  if (end) {
    const endStamp = zonedStamp(end, tz);
    if (endStamp && nowStamp > endStamp) return true;
  }
  return false;
}

function MultiSelectFilter({ labelAll, options, selected, onChange, widthClass = 'w-44' }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selectedSet = useMemo(() => new Set(selected || []), [selected]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (opt) => {
    const next = new Set(selectedSet);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    onChange(Array.from(next));
  };

  const count = selectedSet.size;
  const buttonLabel = count === 0
    ? labelAll
    : count === 1
      ? Array.from(selectedSet)[0]
      : `${count} selected`;

  return (
    <div className={`relative ${widthClass} shrink-0`} ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full min-w-0 items-center justify-between gap-1 rounded-md border border-main-base bg-white px-2 py-1.5 text-left text-xs text-main-primary shadow-sm focus:border-main-primary focus:outline-none focus:ring-1 focus:ring-main-primary"
        title={count ? Array.from(selectedSet).join(', ') : labelAll}
      >
        <span className="min-w-0 truncate font-medium">{buttonLabel}</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-main-light transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-[60] mt-1 max-h-56 w-56 overflow-auto rounded-md border border-main-base bg-white py-1 shadow-lg">
          <button
            type="button"
            onClick={() => onChange([])}
            className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[11px] font-bold uppercase tracking-wider text-main-light hover:bg-main-primary/5 hover:text-main-primary"
          >
            {labelAll}
          </button>
          {options.length === 0 ? (
            <div className="px-2.5 py-2 text-[11px] text-gray-400">No options</div>
          ) : (
            options.map((opt) => {
              const isOn = selectedSet.has(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => toggle(opt)}
                  className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
                >
                  <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${isOn ? 'border-slate-900 bg-slate-900' : 'border-slate-300 bg-white'}`}>
                    {isOn && <Check className="h-2.5 w-2.5 text-white stroke-[3px]" />}
                  </span>
                  <span className={`truncate ${opt === FUNDING_EXPIRED_OPTION ? 'font-bold text-rose-700' : ''}`} title={opt}>{opt}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function HorizontalBubbles({ items }) {
  if (!items?.length) {
    return <span className="text-[10px] text-gray-400">—</span>;
  }
  return (
    <div className="flex items-center gap-1 overflow-x-auto whitespace-nowrap max-w-full [scrollbar-width:thin]">
      {items.map((item, idx) => (
        <span
          key={`${item.key}-${idx}`}
          className={`inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase ${
            item.expired
              ? 'bg-rose-50 text-rose-700 border-rose-200'
              : 'text-slate-500 bg-slate-50 border-slate-100'
          }`}
          title={item.expired ? `${item.label} (expired)` : item.label}
        >
          {item.label}
        </span>
      ))}
    </div>
  );
}

function normalizeDriverTemplateForCompare(entries) {
  if (!entries || !Array.isArray(entries)) return '[]';
  const normalized = [...entries]
    .map((e) => ({
      driver_id: String(e.driver_id ?? ''),
      driver_name: String(e.driver_name ?? ''),
      frozen: !!e.frozen,
      freezePreAssign: !!e.freezePreAssign,
      maxLoadEnabled: !!e.maxLoadEnabled,
      maxLoad: Math.max(0, parseInt(String(e.maxLoad ?? 0), 10) || 0),
    }))
    .sort((a, b) => a.driver_id.localeCompare(b.driver_id));
  return JSON.stringify(normalized);
}

export const DriverSelectionPanel = ({
  isOpen,
  onClose,
  onConfirm,
  schedulerApiUrl,
  customPlanningDate,
  getAuthToken,  // Function to get Firebase auth token
  companyTimezone,  // From sync details (company details) or config; used for "current day" (Freeze eligibility)
  syncTrigger = 0,
  embedded = false, // When true, remove modal-like chrome/shadow sizing
  showConfirmButton = true,
  confirmButtonLabel,
  onSelectionChange,
  /** Mongo/backend company id after sync — required to scope templates per company + API env */
  driverTemplateCompanyId = '',
  driverTemplateServerEnvironment = 'dev',
  mapperShiftsEnabled = false,
  selectedPlannerMapperId = null,
  /** Fleet-based planning: shifts come from the saved Fleet Mapping windows for the planning weekday */
  useFleetMapping = false,
  /** Map of driver_id -> assigned trip count from prefetched preview-trips */
  tripCountByDriverId = null,
  tripsCountLoading = false,
}) => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverIds, setSelectedDriverIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'with_shift', 'without_shift', 'assists', 'farmout'
  const [serviceAreaFilter, setServiceAreaFilter] = useState([]);
  const [fundingFilter, setFundingFilter] = useState([]);
  const [showNoShiftMessage, setShowNoShiftMessage] = useState(false);
  const [serviceAreaMap, setServiceAreaMap] = useState({});
  const [cooperateNames, setCooperateNames] = useState({});

  const [frozenDriverIds, setFrozenDriverIds] = useState(new Set());
  const [freezePreAssignDriverIds, setFreezePreAssignDriverIds] = useState(new Set());
  // Session-only (not saved to template): lock non-selected portal buckets
  const [preservePortalBuckets, setPreservePortalBuckets] = useState(false);
  const [hasSessionForCurrentDay, setHasSessionForCurrentDay] = useState(false);
  /** driver_id -> { enabled, maxLoad } — only enabled entries are sent to the API */
  const [perDriverMaxLoad, setPerDriverMaxLoad] = useState({});

  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);
  const [remoteTemplateRows, setRemoteTemplateRows] = useState([]);
  const [remoteTemplateDocExists, setRemoteTemplateDocExists] = useState(false);
  const [remoteBaselineStr, setRemoteBaselineStr] = useState(null);
  const [templateRemoteLoading, setTemplateRemoteLoading] = useState(false);
  const [templateActionLoading, setTemplateActionLoading] = useState(false);
  const [templateNotice, setTemplateNotice] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, setFirebaseUser);
    return () => unsub();
  }, []);

  const templateScopeReady = useMemo(
    () => !!buildDriverSelectionTemplateDocId(driverTemplateCompanyId, driverTemplateServerEnvironment),
    [driverTemplateCompanyId, driverTemplateServerEnvironment],
  );

  // Planning day in company timezone only (YYYY-MM-DD) for Freeze eligibility. Never use local timezone.
  const tz = companyTimezone || 'America/Los_Angeles';
  const currentDayCompany = useMemo(() => {
    const now = new Date();
    const companyToday = now.toLocaleDateString('en-CA', { timeZone: tz });
    if (!customPlanningDate) return companyToday;
    const selectedInCompany = new Date(customPlanningDate).toLocaleDateString('en-CA', { timeZone: tz });
    if (selectedInCompany === companyToday) return companyToday;
    return selectedInCompany;
  }, [customPlanningDate, tz]);

  const getAuthTokenRef = useRef(getAuthToken);
  useEffect(() => { getAuthTokenRef.current = getAuthToken; });

  const mapperProfileReady = useFleetMapping || !mapperShiftsEnabled || !!selectedPlannerMapperId;

  const fetchDrivers = useCallback(async () => {
    if (!mapperProfileReady) {
      setDrivers([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (customPlanningDate) {
        params.append('custom_datetime_iso', customPlanningDate.toISOString());
      }
      params.append('mapper_shifts_enabled', mapperShiftsEnabled ? 'true' : 'false');
      if (mapperShiftsEnabled && selectedPlannerMapperId) {
        params.append('selected_planner_mapper_id', selectedPlannerMapperId);
      }
      if (useFleetMapping) {
        params.append('use_fleet_mapping', 'true');
      }
      params.append('_t', Date.now()); // Cache busting

      const authToken = getAuthTokenRef.current ? await getAuthTokenRef.current() : null;

      const response = await schedulerApiFetch(`preview-drivers?${params.toString()}`, {
        bearerToken: authToken || undefined,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch drivers: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch drivers');
      }

      setDrivers(data.drivers || []);
      // Auto-select only non-assist drivers with valid shifts
      const driversWithShift = data.drivers?.filter(d => d.has_valid_shift && d.shift_source !== 'none' && d.profile_role !== 'assist') || [];
      setSelectedDriverIds(new Set(driversWithShift.map(d => d.driver_id)));

      // Name maps are a separate fetch so the driver list is not blocked.
      try {
        const [saRes, coopRes] = await Promise.all([
          schedulerApiFetch('service-areas', { cacheBust: true, bearerToken: authToken || undefined }),
          schedulerApiFetch('cooperate-names', { cacheBust: true, bearerToken: authToken || undefined }),
        ]);
        if (saRes.ok) {
          const saJson = await saRes.json();
          if (saJson?.success && saJson?.service_areas) {
            const next = {};
            Object.entries(saJson.service_areas).forEach(([k, v]) => {
              next[String(k).toLowerCase()] = v;
            });
            setServiceAreaMap(next);
          }
        }
        if (coopRes.ok) {
          const coopJson = await coopRes.json();
          if (coopJson?.success && coopJson?.cooperate_names) {
            setCooperateNames(coopJson.cooperate_names);
          }
        }
      } catch {
        // Driver list already loaded; names can stay unresolved.
      }
    } catch (err) {
      console.error('Error fetching drivers:', err);
      setError(err.message);
      if (useFleetMapping) {
        setDrivers([]);
        setSelectedDriverIds(new Set());
      }
    } finally {
      setIsLoading(false);
    }
  }, [customPlanningDate, mapperShiftsEnabled, selectedPlannerMapperId, mapperProfileReady, useFleetMapping]);

  // Preserved is session-only — default off each time the panel opens (not on sync refreshes)
  useEffect(() => {
    if (isOpen) {
      setPreservePortalBuckets(false);
    }
  }, [isOpen]);

  // Fetch drivers when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchDrivers();
    }
  }, [isOpen, fetchDrivers, syncTrigger]);

  // Load per-user driver selection template from Firestore (scoped by company + dev/live)
  useEffect(() => {
    if (!isOpen) return;
    if (!firebaseUser || !templateScopeReady) {
      setRemoteTemplateRows([]);
      setRemoteTemplateDocExists(false);
      setRemoteBaselineStr(null);
      setTemplateRemoteLoading(false);
      return;
    }
    let cancelled = false;
    setTemplateRemoteLoading(true);
    (async () => {
      try {
        const res = await getDriverSelectionTemplate(driverTemplateCompanyId, driverTemplateServerEnvironment);
        if (cancelled) return;
        setRemoteTemplateDocExists(!!res.exists);
        const rows = res.drivers || [];
        setRemoteTemplateRows(rows);
        setRemoteBaselineStr(normalizeDriverTemplateForCompare(rows));
      } catch {
        if (!cancelled) {
          setRemoteTemplateDocExists(false);
          setRemoteTemplateRows([]);
          setRemoteBaselineStr(null);
        }
      } finally {
        if (!cancelled) setTemplateRemoteLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, firebaseUser, syncTrigger, templateScopeReady, driverTemplateCompanyId, driverTemplateServerEnvironment]);

  // Check if the most recent Firebase session is for the current planning day (Freeze only clickable then).
  // Firebase planning_date is already YYYY-MM-DD in company timezone — use as-is, no conversion to local.
  // Compare with currentDayCompany (company timezone only, not local).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const history = await getAssignmentHistory(20);
        if (cancelled) return;
        // Only allow Freeze when the most recent session (by timestamp) is for this planning date
        const mostRecent = Array.isArray(history) && history.length > 0 ? history[0] : null;
        const planningDateStr = (mostRecent?.planning_date ?? '').toString().slice(0, 10); // as-is, company TZ
        const hasSession = mostRecent && planningDateStr === currentDayCompany;
        setHasSessionForCurrentDay(!!hasSession);
      } catch {
        if (!cancelled) setHasSessionForCurrentDay(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, currentDayCompany]);

  const driverAreaItems = useCallback((driver) => {
    const ids = Array.isArray(driver?.service_area_ids) ? driver.service_area_ids : [];
    return ids
      .map((id) => {
        const label = getServiceAreaName(serviceAreaMap, id) || normalizeId(id);
        return label ? { key: normalizeId(id), label } : null;
      })
      .filter(Boolean);
  }, [serviceAreaMap]);

  const driverFundingItems = useCallback((driver) => {
    const rows = Array.isArray(driver?.funding_sources) ? driver.funding_sources : [];
    return rows
      .map((fs) => {
        const id = normalizeId(fs?.id || fs?.name);
        const fromRow = String(fs?.display_name || '').trim();
        const fromMap = getFundingSourceName(cooperateNames, id);
        const label = (fromRow && !looksLikeObjectId(fromRow) ? fromRow : '') || fromMap || (!looksLikeObjectId(id) ? id : '');
        if (!label) return null;
        return { key: id || label, label, expired: isFundingExpired(fs, tz) };
      })
      .filter(Boolean);
  }, [cooperateNames, tz]);

  // Filter drivers based on search and filter mode
  const filteredDrivers = useMemo(() => {
    const safSet = new Set((serviceAreaFilter || []).map((v) => String(v).trim().toLowerCase()).filter(Boolean));
    const fundingSelected = fundingFilter || [];
    const wantsExpired = fundingSelected.includes(FUNDING_EXPIRED_OPTION);
    const fundingNameSet = new Set(
      fundingSelected.filter((v) => v !== FUNDING_EXPIRED_OPTION).map((v) => String(v).trim()).filter(Boolean)
    );

    return drivers.filter(driver => {
      const isAssist = driver.profile_role === 'assist';
      const isFarmout = driver.is_farmout === true || driver.isFarmout === true;

      // Assists tab: show ONLY assist drivers
      if (filterMode === 'assists') return isAssist;

      // Farmout tab: show ONLY farmout drivers
      if (filterMode === 'farmout') return isFarmout;

      // All other tabs: NEVER show assist drivers
      if (isAssist) return false;

      // Search filter
      const matchesSearch = !searchQuery ||
        driver.driver_name.toLowerCase().includes(searchQuery.toLowerCase());

      // Shift filter
      const matchesFilter = filterMode === 'all' ||
        (filterMode === 'with_shift' && driver.has_valid_shift) ||
        (filterMode === 'without_shift' && !driver.has_valid_shift);

      if (!matchesSearch || !matchesFilter) return false;

      if (safSet.size > 0) {
        const areaNames = driverAreaItems(driver).map((item) => item.label.toLowerCase());
        if (!areaNames.some((name) => safSet.has(name))) return false;
      }

      if (fundingNameSet.size > 0 || wantsExpired) {
        const fundingItems = driverFundingItems(driver);
        const matchesName = fundingNameSet.size > 0
          && fundingItems.some((item) => fundingNameSet.has(item.label));
        const matchesExpired = wantsExpired && fundingItems.some((item) => item.expired);
        if (fundingNameSet.size > 0 && wantsExpired) {
          if (!matchesName && !matchesExpired) return false;
        } else if (wantsExpired) {
          if (!matchesExpired) return false;
        } else if (!matchesName) {
          return false;
        }
      }

      return true;
    });
  }, [drivers, searchQuery, filterMode, serviceAreaFilter, fundingFilter, driverAreaItems, driverFundingItems]);

  const serviceAreaOptions = useMemo(() => {
    const s = new Set();
    drivers.forEach((d) => {
      if (d.profile_role === 'assist') return;
      driverAreaItems(d).forEach((item) => {
        if (item.label) s.add(item.label);
      });
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [drivers, driverAreaItems]);

  const fundingOptions = useMemo(() => {
    const s = new Set();
    drivers.forEach((d) => {
      if (d.profile_role === 'assist') return;
      driverFundingItems(d).forEach((item) => {
        if (item.label) s.add(item.label);
      });
    });
    const names = Array.from(s).sort((a, b) => a.localeCompare(b));
    return [FUNDING_EXPIRED_OPTION, ...names];
  }, [drivers, driverFundingItems]);

  // Statistics (assist drivers are always excluded from the main counts)
  const stats = useMemo(() => {
    const nonAssist = drivers.filter(d => d.profile_role !== 'assist');
    return {
      total: nonAssist.length,
      selected: selectedDriverIds.size,
      withShift: nonAssist.filter(d => d.has_valid_shift).length,
      withoutShift: nonAssist.filter(d => !d.has_valid_shift).length,
      assists: drivers.filter(d => d.profile_role === 'assist').length,
      farmout: drivers.filter(d => d.is_farmout === true || d.isFarmout === true).length,
    };
  }, [drivers, selectedDriverIds]);

  const buildTemplateEntriesFromState = useCallback(() => {
    const list = [];
    selectedDriverIds.forEach((id) => {
      const d = drivers.find((x) => x.driver_id === id);
      list.push({
        driver_id: id,
        driver_name: d?.driver_name ?? '',
        frozen: frozenDriverIds.has(id),
        freezePreAssign: freezePreAssignDriverIds.has(id),
        maxLoadEnabled: !!(perDriverMaxLoad[id]?.enabled),
        maxLoad: perDriverMaxLoad[id]?.maxLoad ?? 6,
      });
    });
    return list;
  }, [drivers, selectedDriverIds, frozenDriverIds, freezePreAssignDriverIds, perDriverMaxLoad]);

  const currentTemplateSnapshotStr = useMemo(
    () => normalizeDriverTemplateForCompare(buildTemplateEntriesFromState()),
    [buildTemplateEntriesFromState],
  );

  const isTemplateDirty = remoteTemplateDocExists && currentTemplateSnapshotStr !== remoteBaselineStr;

  const handleSaveDriverTemplate = async () => {
    if (!firebaseUser || !templateScopeReady || selectedDriverIds.size === 0 || templateActionLoading) return;
    const entries = buildTemplateEntriesFromState();
    const wasUpdate = remoteTemplateDocExists;
    setTemplateActionLoading(true);
    setTemplateNotice(null);
    try {
      await saveDriverSelectionTemplate(
        { drivers: entries },
        driverTemplateCompanyId,
        driverTemplateServerEnvironment,
      );
      setRemoteTemplateDocExists(true);
      setRemoteTemplateRows(entries);
      setRemoteBaselineStr(normalizeDriverTemplateForCompare(entries));
      setTemplateNotice({ type: 'ok', message: wasUpdate ? 'Template updated' : 'Template saved' });
    } catch (e) {
      setTemplateNotice({ type: 'err', message: e.message || 'Failed to save template' });
    } finally {
      setTemplateActionLoading(false);
      setTimeout(() => setTemplateNotice(null), 4000);
    }
  };

  const handleUseTemplate = () => {
    if (!remoteTemplateRows.length || !drivers.length) return;
    const byId = new Map(remoteTemplateRows.map((r) => [String(r.driver_id), r]));
    const nextSelected = new Set();
    const nextFrozen = new Set();
    const nextFp = new Set();
    const nextMax = {};
    for (const d of drivers) {
      if (d.profile_role === 'assist') continue;
      const t = byId.get(String(d.driver_id));
      if (!t) continue;
      const hasNoShift = !d.has_valid_shift || d.shift_source === 'none';
      if (hasNoShift) continue;
      nextSelected.add(d.driver_id);
      const fr = !!t.frozen;
      let fp = !!t.freezePreAssign;
      if (fr && fp) fp = false;
      if (fr) nextFrozen.add(d.driver_id);
      if (fp) nextFp.add(d.driver_id);
      if (t.maxLoadEnabled) {
        const n = parseInt(String(t.maxLoad), 10);
        nextMax[d.driver_id] = { enabled: true, maxLoad: Number.isNaN(n) ? 6 : Math.max(0, Math.min(999, n)) };
      }
    }
    setSelectedDriverIds(nextSelected);
    setFrozenDriverIds(nextFrozen);
    setFreezePreAssignDriverIds(nextFp);
    setPerDriverMaxLoad(nextMax);
    setTemplateNotice({ type: 'ok', message: 'Applied saved template' });
    setTimeout(() => setTemplateNotice(null), 4200);
  };

  const handleToggleDriver = (driverId) => {
    // Find the driver to check if they have a valid shift
    const driver = drivers.find(d => d.driver_id === driverId);

    // Check if driver has "No Shift"
    const hasNoShift = !driver?.has_valid_shift || driver?.shift_source === 'none';

    if (hasNoShift) {
      // Show message that this driver cannot be selected
      setShowNoShiftMessage(true);
      setTimeout(() => setShowNoShiftMessage(false), 4200); // Hide after 3 seconds
      return; // Don't allow selection
    }

    setSelectedDriverIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(driverId)) {
        newSet.delete(driverId);
        // When deselecting, remove from Freeze and Freeze Pre Assign too
        setFrozenDriverIds(f => { const n = new Set(f); n.delete(driverId); return n; });
        setFreezePreAssignDriverIds(f => { const n = new Set(f); n.delete(driverId); return n; });
        setPerDriverMaxLoad(prev => {
          const next = { ...prev };
          delete next[driverId];
          return next;
        });
      } else {
        newSet.add(driverId);
      }
      return newSet;
    });
  };



  // Eligible for freeze actions = filtered drivers with valid shift AND selected
  const eligibleForFreeze = useMemo(() =>
    filteredDrivers.filter(d => d.has_valid_shift && d.shift_source !== 'none' && selectedDriverIds.has(d.driver_id)),
    [filteredDrivers, selectedDriverIds]
  );

  const handleFreezeSelectAll = () => {
    if (!hasSessionForCurrentDay) return;
    const ids = new Set(eligibleForFreeze.map(d => d.driver_id));
    setFrozenDriverIds(ids);
    setFreezePreAssignDriverIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  };

  const handleFreezeDeselectAll = () => {
    setFrozenDriverIds(prev => {
      const next = new Set(prev);
      eligibleForFreeze.forEach(d => next.delete(d.driver_id));
      return next;
    });
  };

  const handleFreezePreAssignSelectAll = () => {
    const ids = new Set(eligibleForFreeze.map(d => d.driver_id));
    setFreezePreAssignDriverIds(ids);
    setFrozenDriverIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  };

  const handleFreezePreAssignDeselectAll = () => {
    setFreezePreAssignDriverIds(prev => {
      const next = new Set(prev);
      eligibleForFreeze.forEach(d => next.delete(d.driver_id));
      return next;
    });
  };

  const handleToggleFreeze = (e, driverId) => {
    e.stopPropagation();
    if (!hasSessionForCurrentDay) return; // Freeze only clickable when session exists for current day
    setFrozenDriverIds(prev => {
      const next = new Set(prev);
      if (next.has(driverId)) next.delete(driverId);
      else {
        next.add(driverId);
        setFreezePreAssignDriverIds(prevFp => { const n = new Set(prevFp); n.delete(driverId); return n; });
      }
      return next;
    });
  };

  const handleToggleFreezePreAssign = (e, driverId) => {
    e.stopPropagation();
    setFreezePreAssignDriverIds(prev => {
      const next = new Set(prev);
      if (next.has(driverId)) next.delete(driverId);
      else {
        next.add(driverId);
        setFrozenDriverIds(prevF => { const n = new Set(prevF); n.delete(driverId); return n; });
      }
      return next;
    });
  };

  const toggleMaxLoadEnabled = (e, driverId) => {
    e.stopPropagation();
    setPerDriverMaxLoad(prev => {
      const cur = prev[driverId] || { enabled: false, maxLoad: 6 };
      return {
        ...prev,
        [driverId]: { ...cur, enabled: !cur.enabled, maxLoad: cur.maxLoad ?? 6 },
      };
    });
  };

  const setMaxLoadValue = (e, driverId) => {
    e.stopPropagation();
    const value = e.target.value;
    if (value === '') {
      setPerDriverMaxLoad(prev => ({
        ...prev,
        [driverId]: {
          enabled: prev[driverId]?.enabled ?? false,
          maxLoad: '',
        },
      }));
      return;
    }
    const raw = parseInt(value, 10);
    if (Number.isNaN(raw)) return;
    const maxLoad = Math.max(0, Math.min(999, raw));
    setPerDriverMaxLoad(prev => ({
      ...prev,
      [driverId]: {
        enabled: prev[driverId]?.enabled ?? false,
        maxLoad,
      },
    }));
  };

  const blurMaxLoadValue = (e, driverId) => {
    e.stopPropagation();
    const value = e.target.value;
    if (value === '' || Number.isNaN(parseInt(value, 10))) {
      setPerDriverMaxLoad(prev => ({
        ...prev,
        [driverId]: {
          enabled: prev[driverId]?.enabled ?? false,
          maxLoad: 0,
        },
      }));
    }
  };

  const maxLoadUi = (driverId) => perDriverMaxLoad[driverId] || { enabled: false, maxLoad: 6 };

  const handleConfirm = () => {
    if (selectedDriverIds.size === 0) {
      return; // Don't allow confirming with no drivers
    }

    // If all drivers selected, pass null to use all
    const driverIds = selectedDriverIds.size === drivers.length
      ? null
      : Array.from(selectedDriverIds);
    const frozenIds = Array.from(frozenDriverIds);
    const freezePreAssignIds = Array.from(freezePreAssignDriverIds);

    const perDriverMaxLoadPayload = {};
    const idList = driverIds === null ? drivers.map(d => d.driver_id) : driverIds;
    idList.forEach((id) => {
      const row = perDriverMaxLoad[id];
      if (!row?.enabled) return;
      const n = parseInt(row.maxLoad, 10);
      const maxLoad = Number.isNaN(n) ? 0 : Math.max(0, Math.min(999, n));
      perDriverMaxLoadPayload[id] = { enabled: true, max_load: maxLoad };
    });

    onConfirm(driverIds, frozenIds, freezePreAssignIds, perDriverMaxLoadPayload, preservePortalBuckets);
  };

  const onSelectionChangeRef = useRef(onSelectionChange);
  useEffect(() => { onSelectionChangeRef.current = onSelectionChange; });

  useEffect(() => {
    if (!onSelectionChangeRef.current) return;
    if (drivers.length === 0) return;
    const driverIds = selectedDriverIds.size === drivers.length
      ? null
      : Array.from(selectedDriverIds);
    const perDriverMaxLoadPayload = {};
    const idList = driverIds === null ? drivers.map(d => d.driver_id) : driverIds;
    idList.forEach((id) => {
      const row = perDriverMaxLoad[id];
      if (!row?.enabled) return;
      const n = parseInt(row.maxLoad, 10);
      const maxLoad = Number.isNaN(n) ? 0 : Math.max(0, Math.min(999, n));
      perDriverMaxLoadPayload[id] = { enabled: true, max_load: maxLoad };
    });
    onSelectionChangeRef.current({
      selectedDriverIdsOrNull: driverIds,
      frozenIds: Array.from(frozenDriverIds),
      freezePreAssignIds: Array.from(freezePreAssignDriverIds),
      perDriverMaxLoad: perDriverMaxLoadPayload,
      preservePortalBuckets: !!preservePortalBuckets,
      driverCount: selectedDriverIds.size,
    });
  }, [selectedDriverIds, frozenDriverIds, freezePreAssignDriverIds, perDriverMaxLoad, preservePortalBuckets, drivers]);

  const getShiftSourceBadge = (source) => {
    const badges = {
      'override': { text: 'Override', class: 'bg-purple-100 text-purple-800 border-purple-200' },
      'timeline': { text: 'Timeline', class: 'bg-blue-100 text-blue-800 border-blue-200' },
      'legacy': { text: 'Legacy', class: 'bg-gray-100 text-gray-800 border-gray-200' },
      'mapper': { text: 'Mapper', class: 'bg-teal-100 text-teal-800 border-teal-200' },
      'fleet': { text: 'Fleet based', class: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
      'none': { text: 'No Shift', class: 'bg-red-100 text-red-800 border-red-200' }
    };
    return badges[source] || badges['none'];
  };

  const allSelectableDrivers = useMemo(() => {
    return filteredDrivers.filter(d => d.profile_role !== 'assist' && d.has_valid_shift && d.shift_source !== 'none');
  }, [filteredDrivers]);

  const areAllSelected = allSelectableDrivers.length > 0 && allSelectableDrivers.every(d => selectedDriverIds.has(d.driver_id));
  const areSomeSelected = allSelectableDrivers.some(d => selectedDriverIds.has(d.driver_id));

  const areAllFrozen = eligibleForFreeze.length > 0 && eligibleForFreeze.every(d => frozenDriverIds.has(d.driver_id));
  const areSomeFrozen = eligibleForFreeze.some(d => frozenDriverIds.has(d.driver_id));

  const areAllFreezePreAssign = eligibleForFreeze.length > 0 && eligibleForFreeze.every(d => freezePreAssignDriverIds.has(d.driver_id));
  const areSomeFreezePreAssign = eligibleForFreeze.some(d => freezePreAssignDriverIds.has(d.driver_id));

  const handleToggleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = new Set(selectedDriverIds);
      const newFrozen = new Set(frozenDriverIds);
      const newFreezePreAssign = new Set(freezePreAssignDriverIds);
      const newPerDriverMaxLoad = { ...perDriverMaxLoad };
      
      allSelectableDrivers.forEach(d => {
        newSelected.delete(d.driver_id);
        newFrozen.delete(d.driver_id);
        newFreezePreAssign.delete(d.driver_id);
        delete newPerDriverMaxLoad[d.driver_id];
      });
      
      setSelectedDriverIds(newSelected);
      setFrozenDriverIds(newFrozen);
      setFreezePreAssignDriverIds(newFreezePreAssign);
      setPerDriverMaxLoad(newPerDriverMaxLoad);
    } else {
      const newSelected = new Set(selectedDriverIds);
      allSelectableDrivers.forEach(d => newSelected.add(d.driver_id));
      setSelectedDriverIds(newSelected);
    }
  };

  return (
    <>
      <div className={embedded
        ? "bg-white w-full flex-1 min-h-0 flex flex-col overflow-hidden"
        : "bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden"
      }>

          {/* Header */}
          <div className={`${embedded ? 'px-4 py-3' : 'px-6 py-5'} border-b border-gray-200 bg-gradient-to-r from-slate-50 to-slate-100`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className={`${embedded ? 'text-base' : 'text-xl'} font-bold text-gray-900`}>Select Drivers for Planning</h2>
                {!embedded && (
                  <p className="text-sm text-gray-500 mt-1">
                    Choose which drivers to include in the trip assignment
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className={`flex items-center gap-2 ${embedded ? 'px-2 py-1' : 'px-4 py-2'} bg-white rounded-lg border border-card-border shadow-sm`}>
                  <Users className="w-4 h-4 text-main-primary" />
                  <span className={`${embedded ? 'text-[11px]' : 'text-sm'} font-semibold text-main-dark uppercase tracking-wide`}>
                    {stats.selected} / {stats.total} Selected
                  </span>
                </div>
                {onClose && !embedded && (
                  <button
                    onClick={onClose}
                    className="p-2 text-main-light hover:text-main-primary hover:bg-main-base-100 rounded-lg transition-colors"
                    title="Close without selecting"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Filters Row */}
          <div className={`${embedded ? 'px-4 py-3' : 'px-6 py-4'} border-b border-gray-100 bg-white`}>
            <div className="flex items-center justify-between gap-4">
              {/* Left Side: Search & Filters */}
              <div className="flex items-center gap-4 flex-1 flex-wrap min-w-0">
                {/* Search — fixed width so selecting filters / Clear cannot stretch it */}
                <div className="relative w-[200px] shrink-0">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-main-light" />
                  <input
                    type="text"
                    placeholder="Search drivers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-8 text-[11px] border-card-border rounded-lg shadow-sm focus:border-main-primary focus:ring-main-primary w-full"
                  />
                </div>

                <MultiSelectFilter
                  labelAll="All Service Areas"
                  options={serviceAreaOptions}
                  selected={serviceAreaFilter}
                  onChange={setServiceAreaFilter}
                />
                <MultiSelectFilter
                  labelAll="All funding sources"
                  options={fundingOptions}
                  selected={fundingFilter}
                  onChange={setFundingFilter}
                />
                <button
                  type="button"
                  onClick={() => { setServiceAreaFilter([]); setFundingFilter([]); }}
                  disabled={serviceAreaFilter.length === 0 && fundingFilter.length === 0}
                  className={`px-3 h-8 shrink-0 text-[10px] font-bold uppercase tracking-wider rounded-md border border-main-base bg-white text-main-light hover:text-main-primary hover:border-main-primary hover:bg-main-primary/5 transition-all shadow-sm ${
                    serviceAreaFilter.length === 0 && fundingFilter.length === 0 ? 'invisible pointer-events-none' : ''
                  }`}
                  title="Clear service area and funding filters"
                >
                  Clear
                </button>

                {/* Filter Buttons */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => setFilterMode('all')}
                    className={`px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider font-bold transition-all ${filterMode === 'all'
                      ? 'bg-slate-800 text-white shadow-sm'
                      : 'bg-white text-slate-500 border border-gray-200 hover:bg-gray-50'
                      }`}
                  >
                    All Drivers ({stats.total})
                  </button>
                  <button
                    onClick={() => setFilterMode('with_shift')}
                    className={`px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider font-bold transition-all ${filterMode === 'with_shift'
                      ? 'bg-main-primary text-white shadow-sm'
                      : 'bg-white text-main-primary/60 border border-main-base hover:bg-main-primary/5'
                      }`}
                  >
                    With Shift ({stats.withShift})
                  </button>
                  <button
                    onClick={() => setFilterMode('without_shift')}
                    className={`px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider font-bold transition-all ${filterMode === 'without_shift'
                      ? 'bg-rose-600 text-white shadow-sm'
                      : 'bg-white text-rose-700/60 border border-rose-100 hover:bg-rose-50'
                      }`}
                  >
                    No Shift ({stats.withoutShift})
                  </button>
                  {stats.assists > 0 && (
                    <button
                      onClick={() => setFilterMode('assists')}
                      className={`px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider font-bold transition-all ${filterMode === 'assists'
                        ? 'bg-amber-600 text-white shadow-sm'
                        : 'bg-white text-amber-700/60 border border-amber-100 hover:bg-amber-50'
                        }`}
                    >
                      Assists ({stats.assists})
                    </button>
                  )}
                </div>
              </div>

              {/* Right Side: Primary Action */}
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <label
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] uppercase font-bold border cursor-pointer select-none transition-all ${
                    preservePortalBuckets
                      ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                      : 'border-gray-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                  title="When on: non-selected drivers keep portal buckets locked (no new trips) and are published with a Preserved badge. Not saved to template."
                >
                  <input
                    type="checkbox"
                    checked={preservePortalBuckets}
                    onChange={(e) => setPreservePortalBuckets(e.target.checked)}
                    className="w-3.5 h-3.5 border-slate-300 text-indigo-700 focus:ring-indigo-400 rounded"
                  />
                  Preserved
                </label>
                {firebaseUser && (
                  <>
                    <button
                      type="button"
                      onClick={handleUseTemplate}
                      disabled={
                        !templateScopeReady
                        || templateRemoteLoading
                        || isLoading
                        || remoteTemplateRows.length === 0
                        || drivers.length === 0
                        || templateActionLoading
                      }
                      title={
                        !templateScopeReady
                          ? 'Sync company first so a company ID is available for this template'
                          : remoteTemplateRows.length === 0
                            ? 'Save a template first'
                            : 'Apply saved driver selection and options'
                      }
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] uppercase font-bold border border-main-base bg-white text-main-primary hover:bg-main-primary/5 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      <Bookmark className="w-3.5 h-3.5 shrink-0" />
                      Use Template
                    </button>
                    {!remoteTemplateDocExists ? (
                      <button
                        type="button"
                        onClick={handleSaveDriverTemplate}
                        disabled={
                          !templateScopeReady
                          || templateRemoteLoading
                          || selectedDriverIds.size === 0
                          || templateActionLoading
                        }
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] uppercase font-bold border border-gray-200 bg-white text-slate-800 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        title={!templateScopeReady ? 'Sync company first to save a template for this company and API environment' : undefined}
                      >
                        <BookmarkPlus className="w-3.5 h-3.5 shrink-0" />
                        {templateActionLoading ? 'Saving…' : 'Save Template'}
                      </button>
                    ) : isTemplateDirty ? (
                      <button
                        type="button"
                        onClick={handleSaveDriverTemplate}
                        disabled={
                          !templateScopeReady
                          || templateRemoteLoading
                          || selectedDriverIds.size === 0
                          || templateActionLoading
                        }
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] uppercase font-bold border border-amber-200 bg-amber-50 text-amber-950 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        title={!templateScopeReady ? 'Sync company first to update this template' : undefined}
                      >
                        <BookmarkPlus className="w-3.5 h-3.5 shrink-0" />
                        {templateActionLoading ? 'Updating…' : 'Update Template'}
                      </button>
                    ) : null}
                  </>
                )}

                {showConfirmButton && (
                  <button
                    onClick={handleConfirm}
                    disabled={selectedDriverIds.size === 0 || isLoading}
                    className={`${selectedDriverIds.size === 0 || isLoading
                      ? 'bg-slate-100 text-slate-400 font-bold text-[10px] uppercase cursor-not-allowed opacity-50 px-5 py-2 rounded-xl border border-gray-200'
                      : 'bg-slate-900 text-white font-bold text-[10px] uppercase hover:bg-slate-800 px-5 py-2 rounded-xl shadow-lg transform transition hover:-translate-y-0.5 active:translate-y-0'
                      }`}
                  >
                    {confirmButtonLabel
                      ? confirmButtonLabel
                      : selectedDriverIds.size === drivers.length
                        ? 'Start with All Drivers'
                        : `Start with ${selectedDriverIds.size} Driver${selectedDriverIds.size !== 1 ? 's' : ''}`
                    }
                  </button>
                )}
              </div>
            </div>
            {templateNotice && (
              <div
                className={`mt-2 px-3 py-2 rounded-lg text-[11px] font-semibold ${
                  templateNotice.type === 'ok'
                    ? 'bg-emerald-50 text-emerald-900 border border-emerald-100'
                    : 'bg-red-50 text-red-800 border border-red-100'
                }`}
              >
                {templateNotice.message}
              </div>
            )}
          </div>

          {/* No Shift Message Toast */}
          {showNoShiftMessage && (
            <div
              className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[60]"
              style={{
                animation: 'fadeIn 0.3s ease-in-out'
              }}
            >
              <div className="bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-bold uppercase tracking-wide text-xs">This driver is not eligible due to No Shift</span>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50/50">
            <div className="p-4 flex flex-col gap-4">
            {!mapperProfileReady ? (
              <div className="flex flex-col items-center justify-center py-16 text-center px-6">
                <Users className="w-12 h-12 text-main-base mb-4" />
                <p className="text-main-primary font-bold">Select a fleet mapper profile</p>
                <p className="text-main-light text-sm mt-1 max-w-sm">
                  Mapper mode is on. Choose a profile in the left panel (e.g. Map, All Drivers) before drivers load.
                </p>
              </div>
            ) : isLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-main-primary animate-spin mb-4" />
                <p className="text-main-light font-medium uppercase text-xs tracking-wide">Loading drivers...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-16">
                <AlertTriangle className="w-12 h-12 text-rose-500 mb-4" />
                <p className="text-rose-600 font-bold uppercase">Failed to load drivers</p>
                <p className="text-main-light text-sm mt-1">{error}</p>
                <button
                  onClick={fetchDrivers}
                  className="mt-4 bg-primary text-white px-4 py-2 text-xs uppercase font-bold rounded shadow-sm hover:bg-primary/90"
                >
                  Retry
                </button>
              </div>
            ) : filteredDrivers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Users className="w-12 h-12 text-main-base mb-4" />
                <p className="text-main-light font-bold uppercase">No drivers found</p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-2 text-main-primary text-xs font-bold hover:underline uppercase tracking-wide"
                  >
                    Clear search
                  </button>
                )}
                {(serviceAreaFilter.length > 0 || fundingFilter.length > 0) && (
                  <button
                    onClick={() => { setServiceAreaFilter([]); setFundingFilter([]); }}
                    className="mt-2 text-main-primary text-xs font-bold hover:underline uppercase tracking-wide"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              // Table View
              <div className="shadow ring-1 ring-main-base rounded-lg bg-white overflow-x-auto">
                <table className="w-full min-w-[1420px] table-fixed border-separate border-spacing-0 text-left">
                  <colgroup>
                    <col style={{ width: '48px' }} />
                    <col style={{ width: '180px' }} />
                    {tripCountByDriverId && <col style={{ width: '56px' }} />}
                    <col style={{ width: '160px' }} />
                    <col style={{ width: '160px' }} />
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '250px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '110px' }} />
                  </colgroup>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="sticky top-0 z-30 p-[6px] text-center text-[10px] font-bold text-gray-700 border-b border-r border-gray-200 shadow-sm bg-gray-50 uppercase tracking-wider">
                        <div className="mx-auto flex items-center justify-center cursor-pointer" onClick={handleToggleSelectAll} title={areAllSelected ? "Deselect All" : "Select All"}>
                          <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all ${areAllSelected || areSomeSelected ? 'bg-slate-900 shadow-sm' : 'bg-white border-2 border-slate-200 hover:border-slate-400'}`}>
                            {areAllSelected && <Check className="w-3.5 h-3.5 text-white stroke-[3px]" />}
                            {!areAllSelected && areSomeSelected && <div className="w-2.5 h-[2px] bg-white rounded-sm" />}
                          </div>
                        </div>
                      </th>
                      <th className="sticky top-0 z-40 p-[6px] text-left text-[10px] font-bold text-gray-700 border-b border-r border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)] bg-gray-50 uppercase tracking-wider">Driver</th>
                      {tripCountByDriverId && (
                        <th
                          className="sticky top-0 z-40 p-[6px] text-center text-[10px] font-bold text-gray-700 border-b border-r border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)] bg-gray-50 uppercase tracking-wider"
                          title="Assigned trips in current preview"
                        >
                          <span className="inline-flex items-center justify-center gap-1">
                            Trips
                            {tripsCountLoading && (
                              <Loader2 className="w-3 h-3 animate-spin text-main-primary" aria-hidden="true" />
                            )}
                          </span>
                        </th>
                      )}
                      <th className="sticky top-0 z-40 p-[6px] text-left text-[10px] font-bold text-gray-700 border-b border-r border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)] bg-gray-50 uppercase tracking-wider">Service Area</th>
                      <th className="sticky top-0 z-40 p-[6px] text-left text-[10px] font-bold text-gray-700 border-b border-r border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)] bg-gray-50 uppercase tracking-wider">Funding</th>
                      <th className="sticky top-0 z-40 p-[6px] text-left text-[10px] font-bold text-gray-700 border-b border-r border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)] bg-gray-50 uppercase tracking-wider">Shift Status</th>
                      <th className="sticky top-0 z-40 p-[6px] text-left text-[10px] font-bold text-gray-700 border-b border-r border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)] bg-gray-50 uppercase tracking-wider whitespace-nowrap">Shift Time</th>
                      <th className="sticky top-0 z-40 p-[3px] text-left text-[10px] font-bold text-gray-700 border-b border-r border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)] bg-gray-50 uppercase tracking-wider">
                        <div className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            ref={el => { if (el) el.indeterminate = !areAllFrozen && areSomeFrozen; }}
                            checked={areAllFrozen}
                            disabled={!hasSessionForCurrentDay || eligibleForFreeze.length === 0}
                            onChange={() => areAllFrozen ? handleFreezeDeselectAll() : handleFreezeSelectAll()}
                            title={!hasSessionForCurrentDay ? 'Run planner for today first to enable' : areAllFrozen ? 'Unfreeze all' : `Freeze all ${eligibleForFreeze.length} driver(s)`}
                            className="w-3.5 h-3.5 border-slate-300 text-slate-700 focus:ring-slate-400 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          />
                          <span className="flex items-center gap-1">
                            Freeze
                            {frozenDriverIds.size > 0 && (
                              <span className="normal-case tracking-normal font-bold text-[9px] bg-slate-700 text-white px-1 py-0.5 rounded-full leading-none">{frozenDriverIds.size}</span>
                            )}
                          </span>
                        </div>
                      </th>
                      <th className="sticky top-0 z-40 p-[3px] text-left text-[10px] font-bold text-gray-700 border-b border-r border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)] bg-gray-50 uppercase tracking-wider">
                        <div className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            ref={el => { if (el) el.indeterminate = !areAllFreezePreAssign && areSomeFreezePreAssign; }}
                            checked={areAllFreezePreAssign}
                            disabled={eligibleForFreeze.length === 0}
                            onChange={() => areAllFreezePreAssign ? handleFreezePreAssignDeselectAll() : handleFreezePreAssignSelectAll()}
                            title={areAllFreezePreAssign ? 'Clear pre-assign freeze for all' : `Freeze pre-assign all ${eligibleForFreeze.length} driver(s)`}
                            className="w-3.5 h-3.5 border-slate-300 text-slate-700 focus:ring-slate-400 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          />
                          <span className="flex items-center gap-1">
                            Freeze Pre-Assign
                            {freezePreAssignDriverIds.size > 0 && (
                              <span className="normal-case tracking-normal font-bold text-[9px] bg-slate-700 text-white px-1 py-0.5 rounded-full leading-none">{freezePreAssignDriverIds.size}</span>
                            )}
                          </span>
                        </div>
                      </th>
                      <th className="sticky top-0 z-40 p-[6px] text-left text-[10px] font-bold text-slate-700 border-b border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)] bg-gray-50 uppercase tracking-wider">Max load</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {filteredDrivers.map(driver => {
                      const isAssist = driver.profile_role === 'assist';
                      const isFarmout = driver.is_farmout === true || driver.isFarmout === true;
                      const isSelected = selectedDriverIds.has(driver.driver_id);
                      const isFrozen = frozenDriverIds.has(driver.driver_id);
                      const isFreezePreAssign = freezePreAssignDriverIds.has(driver.driver_id);
                      const shiftBadge = getShiftSourceBadge(driver.shift_source);
                      const hasNoShift = !driver.has_valid_shift || driver.shift_source === 'none';

                      if (isAssist) {
                        return (
                        <tr key={driver.driver_id} className="bg-amber-50 border-b border-gray-300">
                            <td className="p-[3px] border-r border-main-base/10">
                              <div className="flex items-center gap-1 flex-wrap">
                                {isFarmout && (
                                  <span className="px-2 py-0.5 bg-main-primary/10 text-main-primary text-[10px] font-semibold rounded-full border border-main-base">Farmout</span>
                                )}
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded-full border border-amber-300">Assist</span>
                              </div>
                            </td>
                            <td className="p-[3px] border-r border-main-base/10">
                              <div className="flex items-center gap-2">
                                <img
                                  src={driver.profileImageURL}
                                  alt={driver.driver_name}
                                  className="w-6 h-6 rounded-full object-cover border border-amber-200"
                                  onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(driver.driver_name)}&size=24&background=f59e0b&color=fff&rounded=true`; }}
                                />
                                <span className="text-xs font-semibold text-amber-900">{driver.driver_name}</span>
                              </div>
                            </td>
                            {tripCountByDriverId && (
                              <td className="p-[3px] border-r border-main-base/10 text-center">
                                {tripsCountLoading ? (
                                  <span className="inline-block h-4 w-7 rounded bg-amber-200/80 animate-pulse" title="Loading trip counts" />
                                ) : (
                                  <span className="text-[11px] font-bold tabular-nums text-amber-800">
                                    {tripCountByDriverId[String(driver.driver_id)] || 0}
                                  </span>
                                )}
                              </td>
                            )}
                            <td className="p-[3px] border-r border-main-base/10 px-1">
                              <HorizontalBubbles items={driverAreaItems(driver)} />
                            </td>
                            <td className="p-[3px] border-r border-main-base/10 px-1">
                              <HorizontalBubbles items={driverFundingItems(driver)} />
                            </td>
                            <td className="p-[3px]" colSpan={5}>
                              <span className="text-xs font-semibold text-amber-600">Not selectable for planning</span>
                            </td>
                          </tr>
                        );
                      }

                      return (
                         <tr
                          key={driver.driver_id}
                          onClick={() => handleToggleDriver(driver.driver_id)}
                          className={`group text-main-dark border-b border-gray-300 ${hasNoShift
                            ? 'bg-rose-50 cursor-not-allowed opacity-60'
                            : isSelected
                              ? 'bg-main-primary/5 hover:bg-main-primary/10 cursor-pointer'
                              : 'even:bg-main-base-100 hover:bg-slate-50 cursor-pointer text-main-dark'
                            } transition-colors`}
                        >
                          <td className="p-[3px] border-r border-main-base/10 text-center">
                            {!hasNoShift && (
                              <div className={`mx-auto w-5 h-5 rounded-md flex items-center justify-center transition-all ${isSelected ? 'bg-slate-900 shadow-sm' : 'bg-white border-2 border-slate-200 hover:border-slate-400'
                                }`}>
                                {isSelected && <Check className="w-3.5 h-3.5 text-white stroke-[3px]" />}
                              </div>
                            )}
                            {hasNoShift && (
                              <div className="mx-auto w-5 h-5 rounded-md bg-rose-100 flex items-center justify-center border border-rose-200">
                                <X className="w-3.5 h-3.5 text-rose-600 stroke-[3px]" />
                              </div>
                            )}
                          </td>
                          <td className="p-[3px] border-r border-main-base/10">
                            <div className="flex items-center gap-2 min-w-0">
                              <img
                                src={driver.profileImageURL}
                                alt={`${driver.driver_name} profile`}
                                className="w-6 h-6 rounded-full object-cover border border-card-border shrink-0"
                                onError={(e) => {
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(driver.driver_name)}&size=24&background=1f2937&color=fff&rounded=true`;
                                }}
                              />
                              <div className={`text-[11px] font-bold uppercase truncate ${isSelected ? 'text-main-primary' : 'text-main-dark'}`}>
                                {driver.driver_name}
                              </div>
                            </div>
                          </td>
                          {tripCountByDriverId && (
                            <td
                              className="p-[3px] border-r border-main-base/10 text-center"
                              onClick={(e) => e.stopPropagation()}
                              title="Assigned trips in current preview"
                            >
                              {tripsCountLoading ? (
                                <span className="inline-block h-4 w-7 rounded bg-slate-200 animate-pulse" aria-label="Loading trip count" />
                              ) : (
                                <span className={`text-[11px] font-bold tabular-nums ${isSelected ? 'text-main-primary' : 'text-slate-700'}`}>
                                  {tripCountByDriverId[String(driver.driver_id)] || 0}
                                </span>
                              )}
                            </td>
                          )}
                          <td className="p-[3px] border-r border-main-base/10 px-1" onClick={(e) => e.stopPropagation()}>
                            <HorizontalBubbles items={driverAreaItems(driver)} />
                          </td>
                          <td className="p-[3px] border-r border-main-base/10 px-1" onClick={(e) => e.stopPropagation()}>
                            <HorizontalBubbles items={driverFundingItems(driver)} />
                          </td>
                          <td className="p-[3px] border-r border-main-base/10">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`px-1.5 py-0.5 mt-[2px] rounded text-[9px] uppercase font-bold border ${shiftBadge.class}`}>
                                {shiftBadge.text}
                              </span>
                              {isFarmout && (
                               <span className="px-1.5 py-0.5 mt-[2px] rounded text-[9px] uppercase font-bold border bg-main-primary/10 text-main-primary border-main-base">
                                  Farmout
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-[3px] px-1.5 border-r border-main-base/10 align-top overflow-hidden">
                            {driver.has_valid_shift && driver.shift_source === 'fleet' && driver.fleet_windows?.length ? (
                              <div className="flex flex-col gap-0.5 min-w-0">
                                {driver.fleet_windows.map((w) => {
                                  const rig = w.reg_no || w.rig_no || (w.fleet_id ? String(w.fleet_id).slice(-6) : '');
                                  const label = [rig, w.los_title, w.start && w.end ? `(${w.start}–${w.end})` : '']
                                    .filter(Boolean)
                                    .join(' ');
                                  return (
                                    <div
                                      key={`${w.fleet_id}-${w.start}-${w.end}`}
                                      className="text-[10px] leading-snug text-main-dark truncate"
                                      title={label}
                                    >
                                      {rig ? <span className="font-bold">{rig}</span> : null}
                                      {w.los_title ? <span className="text-slate-500"> {w.los_title}</span> : null}
                                      {w.start && w.end ? (
                                        <span className="tabular-nums"> ({w.start}–{w.end})</span>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : driver.has_valid_shift ? (
                              <div className="w-full text-[10px] font-medium tabular-nums truncate">
                                {driver.shift_checkin} – {driver.shift_checkout}
                              </div>
                            ) : (
                              <span className="text-[10px] text-main-base font-bold uppercase">N/A</span>
                            )}
                          </td>
                          <td className="p-[3px] border-r border-main-base/10" onClick={(e) => e.stopPropagation()}>
                            <label className={`inline-flex items-center w-full justify-start h-full ${(isSelected && hasSessionForCurrentDay) ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} title={!isSelected ? 'Select driver first' : hasSessionForCurrentDay ? '' : 'Run planner for today first to enable Freeze'}>
                              <input
                                type="checkbox"
                                checked={isFrozen}
                                disabled={!isSelected || !hasSessionForCurrentDay}
                                onChange={(e) => handleToggleFreeze(e, driver.driver_id)}
                                className="w-3.5 h-3.5 border-slate-300 text-slate-700 focus:ring-slate-400 rounded"
                              />
                            </label>
                          </td>
                          <td className="p-[3px] border-r border-main-base/10" onClick={(e) => e.stopPropagation()}>
                            <label className={`inline-flex items-center w-full justify-start h-full ${isSelected ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} title={!isSelected ? 'Select driver first' : ''}>
                              <input
                                type="checkbox"
                                checked={isFreezePreAssign}
                                disabled={!isSelected}
                                onChange={(e) => handleToggleFreezePreAssign(e, driver.driver_id)}
                                className="w-3.5 h-3.5 border-slate-300 text-slate-700 focus:ring-slate-400 rounded"
                              />
                            </label>
                          </td>
                          <td className="p-[3px]" onClick={(e) => e.stopPropagation()}>
                            <div className={`flex items-center gap-1.5 ${isSelected && !hasNoShift ? '' : 'opacity-50 pointer-events-none'}`}>
                              <label className="flex items-center cursor-pointer mb-0">
                                <input
                                  type="checkbox"
                                  checked={maxLoadUi(driver.driver_id).enabled}
                                  disabled={!isSelected || hasNoShift}
                                  onChange={(e) => toggleMaxLoadEnabled(e, driver.driver_id)}
                                  className="w-3.5 h-3.5 border-slate-400 text-slate-700 focus:ring-slate-400 rounded mr-1.5"
                                />
                              </label>
                              {maxLoadUi(driver.driver_id).enabled && (
                                <input
                                  type="number"
                                  min={0}
                                  max={999}
                                  value={maxLoadUi(driver.driver_id).maxLoad}
                                  onChange={(e) => setMaxLoadValue(e, driver.driver_id)}
                                  onBlur={(e) => blurMaxLoadValue(e, driver.driver_id)}
                                  className="w-12 rounded border-card-border py-0 px-1 text-[11px] h-6 flex-1"
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </div>

          {/* Footer - Hides if embedded so we don't have double footprint */}
            {/* Sub-footer for multi-select status */}
      </div>
    </>
  );
};

const DriverSelectionModal = (props) => {
  const { isOpen, onClose } = props;
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - z-[2100] to appear above sticky header (z-[2000]) */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[2100]" />

      {/* Modal - z-[2100] to appear above sticky header (z-[2000]) */}
      <div className="fixed inset-0 z-[2100] flex items-center justify-center p-4">
        <DriverSelectionPanel {...props} onClose={onClose} />
      </div>
    </>
  );
};

export default DriverSelectionModal;



